import * as React from "react";
import Button from "../Button";
import { KBWidgetConfig, KnowledgeBaseArticle } from "../../types";
import { useState, useCallback, useEffect, useRef } from "react";
import FieldError from "../FieldError";
import KnowledgeBaseService from "../../services/KnowledgeBaseService";
import StoreContainer from "../../stores/StoreContainer";
import KnowledgeBaseMessage from "../KnowledgeBaseMessage";
import { CloseIcon } from "../Icons";

interface Props {
  config: KBWidgetConfig;
}

export default function KBWidget(props: Props) {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] =
    useState<KnowledgeBaseArticle[]>(null);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState(false);
  const mountedRef = useRef(true);
  const resultsRef = useRef<HTMLDivElement>();

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (resultsRef.current) {
      resultsRef.current.scrollIntoView();
    }
  }, [searchResults]);

  const fetchKBResults = useCallback(async () => {
    if (fetching) {
      return;
    }

    setFetching(true);
    setError(false);
    setSearchResults(null);

    let response: any[] = null;
    try {
      response = await KnowledgeBaseService.search(
        StoreContainer.SettingStore.getSiteId(),
        searchTerm
      );
      if (!mountedRef.current) {
        return;
      }
    } catch (e) {
      if (!mountedRef.current) {
        return;
      }
      console.error(e);
      setError(true);
    }

    setFetching(false);

    if (response) {
      setSearchResults(response);
    }
  }, [fetching, searchTerm]);

  if (!props.config || !props.config.Enabled) {
    return null;
  }

  return (
    <>
      <div className="landing-widget kb-widget">
        {props.config.SideImageUrl && (
          <>
            {props.config.SideImageGradientEnabled && (
              <div className="widget-gradient" />
            )}
            <div className="widget-side-img" />
          </>
        )}
        <div className="widget-body">
          <div className="widget-message">{props.config.LabelText}</div>
          <div className="widget-divider" />
          <div className="widget-form-container">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                fetchKBResults();
              }}
            >
              <div className="velaro-form-group">
                <label>{props.config.SearchTermInputLabel}</label>
                <input
                  required
                  minLength={3}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.currentTarget.value)}
                  aria-label={props.config.SearchTermInputLabel}
                />
                {error && (
                  <FieldError>
                    {"We're sorry. There was a problem performing the request."}
                  </FieldError>
                )}
                {searchResults && !searchResults.length && (
                  <FieldError>{"The search returned no results."}</FieldError>
                )}
              </div>
              <Button
                disabled={fetching}
                type="submit"
                className="landing-widget-button"
                variant="primary"
              >
                {fetching ? "Loading..." : props.config.SearchButtonLabel}
              </Button>
            </form>
          </div>
        </div>
      </div>
      {searchResults && !!searchResults.length && (
        <div ref={resultsRef} className="landing-widget kb-results-widget">
          <div className="widget-body">
            <div className="widget-header-actions">
              <button
                onClick={() => setSearchResults(null)}
                role="button"
                aria-label="Close results"
              >
                <CloseIcon color="currentColor" />
              </button>
            </div>
            <KnowledgeBaseMessage
              searchResults={{
                HelpfulButtonLabel: null,
                HelpfulPathId: null,
                NotHelpfulButtonLabel: null,
                NotHelpfulPathId: null,
                Articles: searchResults
              }}
              onClickQuickReply={null}
            />
          </div>
        </div>
      )}
    </>
  );
}
